import { Fragment, ReactNode, useState } from "react";
import { css } from "@emotion/react";
import {
  Colors,
  Dropdown,
  Heading,
  TagLabel,
  Text,
  Typography,
} from "@zapier/design-system";
import { useRendererContext, RichText } from "@zapier/builder.io-kit";
import { emitUserInteractionEvent } from "@/observability/tracking/avroEventTracking/emitUserInteractionEvent";
import {
  EventAction,
  InteractionName,
} from "@/observability/tracking/avroEventTracking/trackingTypes";

import {
  getResponsiveStyles,
  hiddenOnDesktop,
  visibleOnlyOnDesktop,
} from "@/utils/getResponsiveStyles";
import { Slider } from "./Slider";

import type { SliderProps } from "./Slider";

const Styles = {
  positioning: css({
    margin: "auto",
    maxWidth: "1200px",
    padding: "0 20px",
  }),

  spacing: getResponsiveStyles({
    mobile: css({
      marginBottom: "25px",
    }),

    tablet: css({
      marginBottom: "48px",
    }),

    desktop: css({
      marginBottom: "42px",
    }),
  }),

  root: getResponsiveStyles({
    mobile: css({
      display: "grid",
      gap: "12px",
    }),

    tablet: css({
      alignItems: "center",
      display: "grid",
      gridTemplateColumns: "max-content 1fr max-content",
      gridGap: "30px",
    }),

    desktop: css({
      alignItems: "center",
      borderRadius: "8px",
      border: `1px solid ${Colors.neutral400}`,
      padding: "38px 54px",
      display: "grid",
      gridAutoFlow: "column",
      gridTemplateColumns: "1fr max-content",
      gridGap: "38px",
    }),
  }),

  title: css({
    ...Typography.pageHeader9,
    lineHeight: "38px",
  }),

  slider: css({
    gridColumn: "1 / span 2",
    gridRow: "2",
  }),
};

export type TaskTier = {
  label: string;
  numberOfTasks: number;
  showLabel: boolean;
};

export type TaskSelectorProps = {
  children?: ReactNode;
  customTaskLimitTooltip?: string;
  stateKey: string;
  title?: string;
  taskTiers?: Array<TaskTier>;
};

export default function TaskSelector({
  children,
  customTaskLimitTooltip,
  stateKey,
  taskTiers = [],
  title = "How many tasks do you need?",
}: TaskSelectorProps) {
  // Hooks
  const { useStateByKey } = useRendererContext();

  // State
  const [isTouched, setIsTouched] = useState(false);
  const [{ selectedTasks }, setState] = useStateByKey(stateKey, {
    selectedTasks: 0,
  });

  const initialTaskTierIndex = taskTiers?.findIndex(
    (tier) => tier.numberOfTasks === selectedTasks
  );
  const [selectedTaskTierIndex, setSelectedTaskTierIndex] =
    useState(initialTaskTierIndex);

  const sliderSteps: SliderProps["steps"] = [
    {
      label: "0",
      showTick: false,
      showLabel: false,
    },
    ...taskTiers.map((tier) => ({
      label: tier.label,
      showLabel: tier.showLabel,
      showTick: true,
    })),
    {
      label: "Custom task limit",
      showTick: false,
      showLabel: false,
      tooltip: customTaskLimitTooltip && (
        <RichText html={customTaskLimitTooltip} />
      ),
    },
  ];
  const dropdownItems = [
    ...taskTiers.map((tier, index) => ({
      value: index,
      label: `${tier.label} tasks /mo`,
    })),
    {
      value: taskTiers.length,
      label: "Custom task limit",
    },
  ];

  // Event handlers
  function onSliderInput(value: number) {
    onTierChange(value - 1);
  }

  function onSliderChange() {
    maybeEmitChangeEvent();
  }

  function onDropdownChange(item: { value: number; label: string }) {
    onTierChange(item.value);
    maybeEmitChangeEvent();
  }

  // Methods
  function onTierChange(tierIndex: number) {
    let tier: TaskTier = taskTiers[tierIndex];

    if (!tier && tierIndex > 0) {
      tier = {
        numberOfTasks: Infinity,
        showLabel: false,
        label: "Custom",
      };
    }

    setState({
      selectedTasks: tier?.numberOfTasks || 0,
    });
    setSelectedTaskTierIndex(tierIndex);
  }

  function maybeEmitChangeEvent() {
    if (isTouched) return;
    setIsTouched(true);

    emitUserInteractionEvent({
      event_action: EventAction.select,
      interaction_name: InteractionName.task_selector_changed,
      option_selected: "",
    });
  }

  // Computed values
  const isInfinite = selectedTasks === Infinity;
  const displayTaskNumber = isInfinite
    ? taskTiers.at(-1)?.numberOfTasks ?? 0
    : selectedTasks;
  const prettyTaskNumber = `${new Intl.NumberFormat().format(
    displayTaskNumber
  )}${isInfinite ? "+" : ""}`;

  const sliderValue = selectedTasks > 0 ? selectedTaskTierIndex + 1 : 0;
  const dropdownItem = dropdownItems[selectedTaskTierIndex] || null;

  return (
    <div css={[Styles.positioning, Styles.spacing]}>
      <div css={Styles.root}>
        <header css={[visibleOnlyOnDesktop, Styles.title]}>
          <Heading>
            {selectedTasks === 0 && title}
            {selectedTasks > 0 && (
              <Fragment>
                I need{" "}
                <TagLabel color="acidWash">
                  <Text type="pageHeader9">{prettyTaskNumber}</Text>
                </TagLabel>{" "}
                tasks per month
              </Fragment>
            )}
          </Heading>
        </header>

        <Slider
          ariaLabel="Task Selector"
          css={[Styles.slider, visibleOnlyOnDesktop]}
          value={sliderValue}
          min={0}
          max={sliderSteps.length - 1}
          onInput={onSliderInput}
          onChange={onSliderChange}
          steps={sliderSteps}
        />

        <div css={hiddenOnDesktop}>
          <Text type="paragraph1Bold">Select a task tier</Text>
        </div>

        <div css={hiddenOnDesktop}>
          <Dropdown
            ariaLabel="Select a task tier"
            placeholder="Select a task tier"
            menuAriaLabel="List of available task tiers"
            items={dropdownItems}
            onChange={onDropdownChange}
            selectedItem={dropdownItem}
          />
        </div>

        {/* NOTE: Temp until we know more about RG-1455 */}
        <div>{children}</div>
      </div>
    </div>
  );
}
